import { REACT_APP_RINGCENTRAL_SERVER } from '../constants/envConstants';

import { RING_CENTRAL_FRAME_ID } from './ringCentralDialer';

type RcCallParty = {
  phoneNumber?: string;
  extensionNumber?: string;
  name?: string;
  extensionName?: string;
};

export type RingCentralCallLogRecord = {
  id?: string;
  sessionId?: string;
  telephonySessionId?: string;
  direction?: string;
  from?: RcCallParty & { extensionId?: string };
  to?: RcCallParty & { location?: string };
  fromName?: string;
  toName?: string;
  startTime?: number | string;
  duration?: number;
  durationMs?: number;
  result?: string;
  transcript?: string | null;
  recording?: { uri?: string; contentUri?: string; link?: string; id?: string };
};

export type CallLogDbPayload = {
  customer_id: number | null;
  project_id: number | null;
  call_id: string;
  start_time: number;
  duration: number;
  direction: string;
  status: string;
  caller_name: string | null;
  caller_phone: string | null;
  receiver_name: string | null;
  receiver_phone: string | null;
  rc_recording_source?: string | null;
  rc_recording_id?: string | null;
  transcript: string | null;
  all_details: null;
};

const CALL_LOG_FORM_FIELDS = [
  'customer_id',
  'project_id',
  'call_id',
  'start_time',
  'duration',
  'direction',
  'status',
  'caller_name',
  'caller_phone',
  'receiver_name',
  'receiver_phone',
  'transcript',
  'all_details',
] as const;

const RC_TOKEN_STORAGE_KEY = 'sdk-rc-widgetplatform';
const PF_RC_ACCESS_TOKEN_KEY = 'pf-ringcentral-access-token';
const RC_MESSAGE_PREFIX = 'rc-adapter-message';
const RC_WIDGET_TOKEN_PATHS = [
  '/access-token',
  '/platform/access-token',
  '/get-access-token',
  '/account/access-token',
  '/platform-info',
];
const RC_WIDGET_RECORDING_PATHS = [
  '/recording/content',
  '/account/recording/content',
];
const WIDGET_REQUEST_TIMEOUT_MS = 20000;

let priorCallLogSyncToken: string | null = null;

const readStoredCallLogSyncToken = (): string | null => {
  try {
    const raw = localStorage.getItem(RC_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const platform = JSON.parse(raw) as {
      callLogSyncToken?: string;
      syncToken?: string;
    };
    return platform.callLogSyncToken || platform.syncToken || null;
  } catch {
    return null;
  }
};

if (typeof window !== 'undefined') {
  priorCallLogSyncToken = readStoredCallLogSyncToken();
}

export const getPriorCallLogSyncToken = (): string | null => {
  return priorCallLogSyncToken;
};

/** Re-read sync token from widget localStorage (widget may have updated it after ISync). */
export const refreshPriorCallLogSyncTokenFromStorage = (): string | null => {
  const stored = readStoredCallLogSyncToken();
  if (stored) {
    priorCallLogSyncToken = stored;
  }
  return stored;
};

export const getCanonicalCallKey = (
  record:
    | RingCentralCallLogRecord
    | { telephonySessionId?: string; sessionId?: string; id?: string }
): string | null => {
  const telephony = record.telephonySessionId?.trim();
  if (telephony) return telephony;
  const session = record.sessionId?.trim();
  if (session) return session;
  const id = record.id?.trim();
  if (id && !id.startsWith('p-')) return id;
  return null;
};

export const resolveCrmCallId = getCanonicalCallKey;

export const collectCallDedupeKeys = (
  record?:
    | RingCentralCallLogRecord
    | { telephonySessionId?: string; sessionId?: string; id?: string }
    | null,
  callId?: string | null
): string[] => {
  const keys = new Set<string>();
  const candidates = [
    callId,
    record?.telephonySessionId,
    record?.sessionId,
    record?.id,
    record ? getCanonicalCallKey(record) : null,
  ];

  candidates.forEach(value => {
    const trimmed = String(value || '').trim();
    if (!trimmed || trimmed.startsWith('p-')) return;
    keys.add(trimmed);
  });

  return Array.from(keys);
};

const extractAccessTokenFromUri = (uri: string): string | null => {
  try {
    return new URL(uri).searchParams.get('access_token');
  } catch {
    const match = uri.match(/[?&]access_token=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
};

const parseStoredRingCentralToken = (raw: string): string | null => {
  try {
    const parsed = JSON.parse(raw) as {
      access_token?: string;
      accessToken?: string;
      token?: { access_token?: string; accessToken?: string };
    };
    return (
      parsed.access_token ||
      parsed.accessToken ||
      parsed.token?.access_token ||
      parsed.token?.accessToken ||
      null
    );
  } catch {
    return null;
  }
};

export const mirrorRingCentralAccessToken = (token: string) => {
  if (!token) return;
  try {
    localStorage.setItem(PF_RC_ACCESS_TOKEN_KEY, token);
  } catch {
    return;
  }
};

export const getRingCentralAccessToken = (): string | null => {
  try {
    const mirrored = localStorage.getItem(PF_RC_ACCESS_TOKEN_KEY);
    if (mirrored) return mirrored;

    const raw = localStorage.getItem(RC_TOKEN_STORAGE_KEY);
    if (!raw) return null;

    const token = parseStoredRingCentralToken(raw);
    if (token) {
      mirrorRingCentralAccessToken(token);
      return token;
    }
  } catch {
    return null;
  }
  return null;
};

const resolveRingCentralAccessToken = async (): Promise<string | null> =>
  getRingCentralAccessToken() ??
  (await fetchRingCentralAccessTokenFromWidget());

type RecordingLookup = Pick<
  RingCentralCallLogRecord,
  'id' | 'sessionId' | 'telephonySessionId'
>;

const tokenizedRecordingUris = new Map<string, string>();

const getRecordingCacheKeys = (call: RecordingLookup): string[] =>
  [call.telephonySessionId, call.sessionId, call.id].filter(
    Boolean
  ) as string[];

export const isTokenizedRecordingUri = (uri: string) =>
  uri.includes('access_token=');

export const cacheTokenizedRecording = (call: RingCentralCallLogRecord) => {
  const uri = resolveRecordingContentUri(call.recording, call);
  if (!uri) return;

  const token = extractAccessTokenFromUri(uri);
  if (token) mirrorRingCentralAccessToken(token);

  if (!isTokenizedRecordingUri(uri)) return;

  getRecordingCacheKeys(call).forEach(key =>
    tokenizedRecordingUris.set(key, uri)
  );
};

const getCachedTokenizedRecordingUri = (
  call?: RecordingLookup
): string | null => {
  if (!call) return null;
  for (const key of getRecordingCacheKeys(call)) {
    const uri = tokenizedRecordingUris.get(key);
    if (uri) return uri;
  }
  return null;
};

const unwrapMediaReaderUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    if (
      parsed.hostname.includes('ringcentral.github.io') ||
      parsed.pathname.includes('ringcentral-media-reader')
    ) {
      const media = parsed.searchParams.get('media');
      if (media) return decodeURIComponent(media);
    }
  } catch {
    return url;
  }
  return url;
};

const isPlayerOrWrapperUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname.includes('ringcentral.github.io') ||
      parsed.pathname.includes('ringcentral-media-reader') ||
      parsed.pathname.includes('/integrations/recording/')
    );
  } catch {
    return false;
  }
};

export const resolveRecordingContentUri = (
  recording?: RingCentralCallLogRecord['recording'] | null,
  call?: RecordingLookup
): string | null => {
  const cached = getCachedTokenizedRecordingUri(call);
  if (cached) return unwrapMediaReaderUrl(cached);
  if (!recording) return null;

  const candidates = [
    recording.contentUri,
    recording.uri,
    recording.link,
  ].filter(Boolean) as string[];

  for (const candidate of candidates) {
    const unwrapped = unwrapMediaReaderUrl(candidate);
    if (isPlayerOrWrapperUrl(unwrapped)) continue;
    if (unwrapped.includes('/recording/')) {
      return unwrapped.includes('/content')
        ? unwrapped
        : `${unwrapped.replace(/\/$/, '')}/content`;
    }
  }

  for (const candidate of candidates) {
    const mediaUrl = unwrapMediaReaderUrl(candidate);
    if (
      mediaUrl.includes('media.ringcentral.com') &&
      mediaUrl.includes('/content')
    ) {
      return mediaUrl;
    }
  }

  return null;
};

const isValidRecordingBlob = (blob: Blob): boolean => {
  if (!blob || blob.size < 256) return false;
  const type = (blob.type || '').toLowerCase();
  if (type.includes('html') || type.includes('json') || type.includes('text')) {
    return false;
  }
  return (
    type.startsWith('audio/') ||
    type === 'application/octet-stream' ||
    type === 'video/mp4'
  );
};

const withAccessTokenQuery = (recordingUri: string, token: string): string => {
  try {
    const url = new URL(recordingUri);
    if (!url.searchParams.has('access_token')) {
      url.searchParams.set('access_token', token);
    }
    return url.toString();
  } catch {
    const separator = recordingUri.includes('?') ? '&' : '?';
    return `${recordingUri}${separator}access_token=${encodeURIComponent(token)}`;
  }
};

const normalizeRecordingContentUri = (recordingUri: string): string => {
  const unwrapped = unwrapMediaReaderUrl(recordingUri);
  if (isPlayerOrWrapperUrl(unwrapped)) {
    return unwrapped;
  }
  return unwrapped.includes('/content')
    ? unwrapped
    : `${unwrapped.replace(/\/$/, '')}/content`;
};

const getRcServer = (): string | null => {
  const configured = REACT_APP_RINGCENTRAL_SERVER?.trim().replace(/\/$/, '');
  return configured || null;
};

export const isRingCentralRecordingUrl = (recordingUri: string): boolean => {
  try {
    const { hostname } = new URL(recordingUri);
    if (hostname.includes('ringcentral.com')) return true;
    const server = getRcServer();
    if (server) {
      return new URL(server).hostname === hostname;
    }
  } catch {
    return false;
  }
  return false;
};

const fetchRecordingUrlAsBlob = async (
  contentUri: string,
  token: string | null
): Promise<Blob | null> => {
  const hasTokenInUrl = isTokenizedRecordingUri(contentUri);
  const attempts: Array<{ url: string; useBearer: boolean }> = [];

  if (hasTokenInUrl) {
    attempts.push({ url: contentUri, useBearer: false });
  } else if (token) {
    attempts.push({ url: contentUri, useBearer: true });
    attempts.push({
      url: withAccessTokenQuery(contentUri, token),
      useBearer: false,
    });
  } else {
    attempts.push({ url: contentUri, useBearer: false });
  }

  for (const { url, useBearer } of attempts) {
    try {
      const response = await fetch(url, {
        ...(useBearer && token
          ? { headers: { Authorization: `Bearer ${token}` } }
          : {}),
      });
      if (!response.ok) {
        continue;
      }
      const blob = await response.blob();
      if (isValidRecordingBlob(blob)) return blob;
    } catch {
      continue;
    }
  }

  return null;
};

export const fetchCallRecordingBlob = async (
  recordingUri: string,
  recordingId?: string | null
): Promise<Blob | null> => {
  const contentUri = normalizeRecordingContentUri(recordingUri);
  if (isPlayerOrWrapperUrl(contentUri)) {
    return null;
  }

  const hasTokenInUrl = isTokenizedRecordingUri(contentUri);
  if (hasTokenInUrl) {
    const embeddedToken = extractAccessTokenFromUri(contentUri);
    if (embeddedToken) mirrorRingCentralAccessToken(embeddedToken);
  }

  let token = hasTokenInUrl
    ? extractAccessTokenFromUri(contentUri)
    : await resolveRingCentralAccessToken();

  if (isRingCentralRecordingUrl(contentUri) && !hasTokenInUrl && !token) {
    token = await fetchRingCentralAccessTokenFromWidget();
  }

  if (token || hasTokenInUrl || !isRingCentralRecordingUrl(contentUri)) {
    const blob = await fetchRecordingUrlAsBlob(contentUri, token);
    if (blob) return blob;
  }

  const fromWidget = await fetchRecordingBlobFromWidget(
    contentUri,
    recordingId
  );
  if (fromWidget && isValidRecordingBlob(fromWidget)) return fromWidget;

  return null;
};

export const buildCallLogPostBody = (
  payload: CallLogDbPayload,
  recording?: Blob | null
): FormData => {
  const formData = new FormData();

  CALL_LOG_FORM_FIELDS.forEach(key => {
    const value = payload[key];
    formData.append(
      key,
      value === null || value === undefined ? '' : String(value)
    );
  });

  if (recording && recording.size > 0) {
    formData.append(
      'recording_uri',
      recording,
      `recording-${payload.call_id}.mp3`
    );
  }

  return formData;
};

export const mapRcCallLogToDbPayload = (
  call: RingCentralCallLogRecord,
  customerId?: number | null,
  projectId?: number | null
): CallLogDbPayload | null => {
  const callId = resolveCrmCallId(call) || '';
  if (!callId) {
    return null;
  }

  const startTimeMs =
    typeof call.startTime === 'number'
      ? call.startTime
      : call.startTime
        ? new Date(call.startTime).getTime()
        : Date.now();
  const callerPhone =
    call.from?.phoneNumber || call.from?.extensionNumber || null;
  const receiverPhone =
    call.to?.phoneNumber || call.to?.extensionNumber || null;

  return {
    customer_id: customerId ?? null,
    project_id: projectId ?? null,
    call_id: callId,
    start_time: Math.floor(startTimeMs / 1000),
    duration: call.duration ?? Math.floor((call.durationMs || 0) / 1000),
    direction: call.direction || '',
    status: call.result || '',
    caller_name:
      call.from?.name ||
      call.fromName ||
      call.from?.extensionName ||
      callerPhone,
    caller_phone: callerPhone,
    receiver_name:
      call.to?.name || call.toName || call.to?.extensionName || receiverPhone,
    receiver_phone: receiverPhone,
    rc_recording_source: resolveRecordingContentUri(call.recording, call),
    rc_recording_id: call.recording?.id || null,
    transcript: call.transcript || null,
    all_details: null,
  };
};

export const persistCallLogSyncToken = (syncToken: string) => {
  if (!syncToken) return;
  try {
    const raw = localStorage.getItem(RC_TOKEN_STORAGE_KEY);
    const platform = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    platform.callLogSyncToken = syncToken;
    platform.syncToken = syncToken;
    localStorage.setItem(RC_TOKEN_STORAGE_KEY, JSON.stringify(platform));
    priorCallLogSyncToken = syncToken;
  } catch {
    return;
  }
};

export const getStoredCallLogSyncToken = (): string | null =>
  readStoredCallLogSyncToken();

export type CallLogSyncResponse = {
  records?: RingCentralCallLogRecord[];
  syncInfo?: { syncType?: string; syncToken?: string };
};

export const fetchCallLogSync = async (
  syncToken: string
): Promise<CallLogSyncResponse | null> => {
  const token = await resolveRingCentralAccessToken();
  const server = getRcServer();
  if (!token || !server || !syncToken) {
    return null;
  }

  try {
    const query = new URLSearchParams({
      syncType: 'ISync',
      syncToken,
      showDeleted: 'false',
    });
    const url = `${server}/restapi/v1.0/account/~/extension/~/call-log-sync?${query}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      return null;
    }
    const body = (await response.json()) as CallLogSyncResponse;
    return body;
  } catch {
    return null;
  }
};

export const fetchCallLogBySessionId = async (
  sessionKey: string
): Promise<RingCentralCallLogRecord | null> => {
  const token = await resolveRingCentralAccessToken();
  const server = getRcServer();
  if (!token || !server || !sessionKey) {
    return null;
  }

  const queries = sessionKey.startsWith('s-')
    ? [
        new URLSearchParams({
          view: 'Detailed',
          telephonySessionId: sessionKey,
        }),
      ]
    : [
        new URLSearchParams({ view: 'Detailed', sessionId: sessionKey }),
        new URLSearchParams({
          view: 'Detailed',
          telephonySessionId: sessionKey,
        }),
      ];

  for (const query of queries) {
    const url = `${server}/restapi/v1.0/account/~/extension/~/call-log?${query}`;
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        continue;
      }
      const body = (await response.json()) as {
        records?: RingCentralCallLogRecord[];
      };
      const record = body.records?.[0];
      if (record) {
        return record;
      }
    } catch {
      continue;
    }
  }

  return null;
};

const CALL_LOG_API_RETRY_MS = [0, 2000, 4000, 6000];

export type CallLogLookup = {
  telephonySessionId?: string | null;
  sessionId?: string | null;
  partyId?: string | null;
};

export const resolveCallLogRecordFromApi = async (
  lookup: CallLogLookup,
  getLookup?: () => CallLogLookup
): Promise<RingCentralCallLogRecord | null> => {
  for (const delayMs of CALL_LOG_API_RETRY_MS) {
    if (delayMs) {
      await new Promise<void>(resolve => {
        window.setTimeout(resolve, delayMs);
      });
    }

    const activeLookup = getLookup ? getLookup() : lookup;
    const keys = [
      activeLookup.telephonySessionId,
      activeLookup.sessionId,
      activeLookup.partyId,
    ].filter(Boolean) as string[];

    await resolveRingCentralAccessToken();
    refreshPriorCallLogSyncTokenFromStorage();

    for (const key of keys) {
      const record = await fetchCallLogBySessionId(key);
      if (record && shouldSyncCallLogRecord(record)) {
        return record;
      }
    }

    const priorToken = getPriorCallLogSyncToken();
    if (priorToken) {
      const sync = await fetchCallLogSyncDelta(priorToken);
      if (sync?.syncInfo?.syncToken) {
        persistCallLogSyncToken(sync.syncInfo.syncToken);
      }
      const records = sync?.records || [];
      if (records.length) {
        const match = records.find(record => {
          const recordKeys = [
            record.telephonySessionId,
            record.sessionId,
            record.id,
          ].filter(Boolean);
          return recordKeys.some(id => keys.includes(String(id)));
        });
        if (match && shouldSyncCallLogRecord(match)) {
          return match;
        }
        const ready = records.filter(shouldSyncCallLogRecord);
        if (ready.length) {
          return ready[ready.length - 1];
        }
      }
    }
  }

  return null;
};

/** Fetch call-log delta using the prior sync token (ISync returns records since that token). */
export const fetchBootstrapCallLogSync =
  async (): Promise<CallLogSyncResponse | null> => {
    const accessToken = await resolveRingCentralAccessToken();
    const server = getRcServer();
    if (!accessToken || !server) {
      return null;
    }

    try {
      const query = new URLSearchParams({
        syncType: 'FSync',
        recordCount: '100',
        showDeleted: 'false',
      });
      const url = `${server}/restapi/v1.0/account/~/extension/~/call-log-sync?${query}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      });
      if (!response.ok) {
        return null;
      }
      const body = (await response.json()) as CallLogSyncResponse;
      return body;
    } catch {
      return null;
    }
  };

export const fetchCallLogSyncDelta = async (
  priorSyncToken?: string | null
): Promise<CallLogSyncResponse | null> => {
  const token = priorSyncToken ?? getPriorCallLogSyncToken();
  if (!token) {
    return fetchBootstrapCallLogSync();
  }

  const sync = await fetchCallLogSync(token);
  if (!sync) return null;

  const nextToken = sync.syncInfo?.syncToken;
  if (!sync.records?.length && nextToken && nextToken !== token) {
    const followUp = await fetchCallLogSync(nextToken);
    if (followUp?.records?.length) {
      return followUp;
    }
  }

  return sync;
};

export const fetchUnloggedCallsViaWidget = async (): Promise<
  RingCentralCallLogRecord[]
> => {
  try {
    const response = await requestWidget<{
      calls?: RingCentralCallLogRecord[];
    }>('/unlogged-calls', { perPage: 25, page: 1 });
    return response?.calls || [];
  } catch {
    return [];
  }
};

const normalizeCallParty = (
  party?: string | RingCentralCallLogRecord['from']
): RingCentralCallLogRecord['from'] => {
  if (!party) return undefined;
  if (typeof party === 'string') {
    const trimmed = party.trim();
    if (!trimmed) return undefined;
    return { phoneNumber: trimmed };
  }
  return party;
};

export const mapWidgetCallToLogRecord = (
  call: RingCentralCallLogRecord & {
    callerId?: string;
    telephonyStatus?: string;
    callId?: string;
    callStatus?: string;
    from?: string | RingCentralCallLogRecord['from'];
    to?: string | RingCentralCallLogRecord['to'];
  },
  sessionHint?: {
    telephonySessionId?: string | null;
    sessionId?: string | null;
  } | null
): RingCentralCallLogRecord => {
  const telephonySessionId =
    call.telephonySessionId ||
    sessionHint?.telephonySessionId ||
    (call.sessionId?.startsWith('s-') ? call.sessionId : undefined);
  const sessionId =
    (call.sessionId && !call.sessionId.startsWith('s-')
      ? call.sessionId
      : undefined) ||
    sessionHint?.sessionId ||
    call.callId ||
    (call.id && !call.id.startsWith('p-') && !call.id.startsWith('s-')
      ? call.id
      : undefined);
  const from = normalizeCallParty(call.from);
  const to = normalizeCallParty(call.to);

  return {
    id: call.id,
    sessionId,
    telephonySessionId,
    direction: call.direction,
    from,
    to,
    fromName: call.fromName || from?.name,
    toName: call.toName || to?.name,
    startTime: call.startTime,
    duration: call.duration,
    durationMs: call.durationMs,
    result: call.result || call.telephonyStatus || call.callStatus,
    transcript: call.transcript ?? null,
    recording: call.recording,
  };
};

const getRecordPhone = (record: RingCentralCallLogRecord): string | null => {
  const fromPhone =
    record.from?.phoneNumber || record.from?.extensionNumber || null;
  const toPhone = record.to?.phoneNumber || record.to?.extensionNumber || null;
  return fromPhone || toPhone;
};

const isInterimCallLogResult = (result: string) => {
  const status = result.toLowerCase().trim();
  if (!status) return false;
  return (
    status === 'disconnecting' ||
    status.includes('ringing') ||
    status === 'proceeding' ||
    status === 'setup' ||
    status === 'answering' ||
    status.includes('webphone-session') ||
    status.includes('webphone')
  );
};

export const shouldSyncCallLogRecord = (
  record: RingCentralCallLogRecord
): boolean => {
  const callId = resolveCrmCallId(record);
  if (!callId) {
    return false;
  }

  const result = (record.result || '').toLowerCase().trim();
  if (result && isInterimCallLogResult(result)) {
    return false;
  }

  if (result === 'call connected' && !record.duration && !record.durationMs) {
    return false;
  }

  if (!getRecordPhone(record)) {
    return false;
  }

  return true;
};

export const matchesCallLogLookup = (
  record: RingCentralCallLogRecord,
  lookup: CallLogLookup
): boolean => {
  const keys = [
    lookup.telephonySessionId,
    lookup.sessionId,
    lookup.partyId,
  ].filter(Boolean) as string[];
  if (!keys.length) return true;

  const recordKeys = [
    record.telephonySessionId,
    record.sessionId,
    record.id,
  ].filter(Boolean) as string[];

  return recordKeys.some(id => keys.includes(String(id)));
};

export const fetchCallLogRecordsAfterHistorySync = async (
  lookup?: CallLogLookup | null
): Promise<RingCentralCallLogRecord[]> => {
  await syncRingCentralAccessTokenFromWidget();
  let records = await fetchUnloggedCallsViaWidget();
  if (!records.length) {
    const widgetSync = await fetchCallLogSyncViaWidget();
    if (widgetSync?.syncInfo?.syncToken) {
      persistCallLogSyncToken(widgetSync.syncInfo.syncToken);
    }
    records = widgetSync?.records || [];
  }

  if (lookup && records.length) {
    const matched = records.filter(record =>
      matchesCallLogLookup(record, lookup)
    );
    if (matched.length) {
      return matched;
    }
  }

  return records;
};

export const isCallLogRecordReady = (record: RingCentralCallLogRecord) =>
  shouldSyncCallLogRecord(record);

export const getRecordStartTimeMs = (
  record: RingCentralCallLogRecord
): number => {
  if (typeof record.startTime === 'number') {
    return record.startTime < 1e12 ? record.startTime * 1000 : record.startTime;
  }
  if (record.startTime) {
    return new Date(record.startTime).getTime();
  }
  return Date.now();
};

export const filterRecentSyncRecords = (
  records: RingCentralCallLogRecord[],
  maxAgeMs = 24 * 60 * 60 * 1000
): RingCentralCallLogRecord[] => {
  const cutoff = Date.now() - maxAgeMs;
  const recent = records.filter(
    record => getRecordStartTimeMs(record) >= cutoff
  );
  return recent;
};

const requestWidget = <T>(
  path: string,
  body: Record<string, unknown> = {},
  timeout = WIDGET_REQUEST_TIMEOUT_MS
): Promise<T> =>
  new Promise((resolve, reject) => {
    const frame = document.getElementById(
      RING_CENTRAL_FRAME_ID
    ) as HTMLIFrameElement | null;
    if (!frame?.contentWindow) {
      reject(new Error('RingCentral Phone is not loaded.'));
      return;
    }

    const requestId = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

    const onMessage = (event: MessageEvent) => {
      if (event.source !== frame.contentWindow) return;
      const data = event.data as {
        type?: string;
        responseId?: string;
        response?: T;
      };
      if (
        data?.type === `${RC_MESSAGE_PREFIX}-response` &&
        data.responseId === requestId
      ) {
        window.removeEventListener('message', onMessage);
        window.clearTimeout(timer);
        resolve(data.response as T);
      }
    };

    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      reject(new Error('RingCentral Phone did not respond.'));
    }, timeout);

    window.addEventListener('message', onMessage);
    frame.contentWindow.postMessage(
      { type: `${RC_MESSAGE_PREFIX}-request`, requestId, path, body },
      '*'
    );
  });

export const fetchCallLogSyncViaWidget =
  async (): Promise<CallLogSyncResponse | null> => {
    try {
      const response = await requestWidget<CallLogSyncResponse>(
        '/call-log-sync',
        {},
        10000
      );
      return response;
    } catch {
      return null;
    }
  };

const decodeWidgetBlob = (response: {
  blob?: string;
  data?: string;
  content?: string;
  contentType?: string;
}): Blob | null => {
  const raw = response.blob || response.data || response.content;
  if (!raw || typeof raw !== 'string') return null;

  try {
    const base64 = raw.includes(',') ? raw.split(',')[1] : raw;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], {
      type: response.contentType || 'audio/mpeg',
    });
  } catch {
    return null;
  }
};

const extractWidgetAccessToken = (
  response: unknown,
  depth = 0
): string | null => {
  if (response == null || depth > 12) return null;

  if (typeof response === 'string') {
    if (response === 'no matched path' || response.length < 20) return null;
    const uriMatch = response.match(/[?&]access_token=([^&]+)/);
    if (uriMatch?.[1]) return decodeURIComponent(uriMatch[1]);
    try {
      return extractWidgetAccessToken(JSON.parse(response), depth + 1);
    } catch {
      return null;
    }
  }

  if (typeof response !== 'object') return null;

  const record = response as Record<string, unknown>;
  for (const key of ['access_token', 'accessToken']) {
    const token = record[key];
    if (typeof token === 'string' && token.length > 20) return token;
  }

  if (record.token && typeof record.token === 'object') {
    const nested = extractWidgetAccessToken(record.token, depth + 1);
    if (nested) return nested;
  }

  for (const key of ['data', 'platform', 'auth', 'credentials', 'response']) {
    const nested = record[key];
    if (nested != null) {
      const token = extractWidgetAccessToken(nested, depth + 1);
      if (token) return token;
    }
  }

  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') {
      const token = extractWidgetAccessToken(value, depth + 1);
      if (token) return token;
    }
  }

  return null;
};

const fetchRecordingBlobFromWidget = async (
  recordingUri: string,
  recordingId?: string | null
): Promise<Blob | null> => {
  const contentUri = normalizeRecordingContentUri(recordingUri);
  const bodies: Record<string, unknown>[] = [
    { contentUri, uri: contentUri, recordingUri: contentUri },
  ];
  if (recordingId) {
    bodies.push({ recordingId, id: recordingId });
  }

  for (const path of RC_WIDGET_RECORDING_PATHS) {
    for (const body of bodies) {
      try {
        const response = await requestWidget<{
          blob?: string;
          data?: string;
          content?: string;
          contentType?: string;
        }>(path, body);
        const blob = decodeWidgetBlob(response);
        if (blob && isValidRecordingBlob(blob)) {
          return blob;
        }
      } catch {
        continue;
      }
    }
  }

  return null;
};

let widgetTokenFetchPromise: Promise<string | null> | null = null;

export const fetchRingCentralAccessTokenFromWidget = async (): Promise<
  string | null
> => {
  const existing = getRingCentralAccessToken();
  if (existing) return existing;

  if (widgetTokenFetchPromise) return widgetTokenFetchPromise;

  widgetTokenFetchPromise = (async () => {
    for (const path of RC_WIDGET_TOKEN_PATHS) {
      try {
        const response = await requestWidget<unknown>(path, {}, 5000);
        const token = extractWidgetAccessToken(response);
        if (token) {
          mirrorRingCentralAccessToken(token);
          return token;
        }
      } catch {
        continue;
      }
    }
    return null;
  })();

  try {
    return await widgetTokenFetchPromise;
  } finally {
    widgetTokenFetchPromise = null;
  }
};

export const syncRingCentralAccessTokenFromWidget = async (): Promise<
  string | null
> => fetchRingCentralAccessTokenFromWidget();
